import { breakpoints, events } from '@theme/constants';
import { getAttributeValue, getRefs, morph, parseHtml } from '@theme/dom';
import { isFromBreakpoint, isUntilBreakpoint, throttle } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

class ProductGallery extends HTMLCustomElement {
  animation = {
    duration: getAttributeValue(this, 'autoscroll-speed', 36000),
    easing: (transition) => transition,
  };

  async connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    // eslint-disable-next-line import/no-unresolved
    const { default: KeenSlider } = await import('keen-slider');

    if (isFromBreakpoint('large')) {
      if (this.refs.slider.children.length === 2) {
        this.originalChildren = Array.from(this.refs.slider.children);

        for (const slide of Array.from(this.refs.slider.children)) {
          this.refs.slider.appendChild(slide.cloneNode(true));
        }
      }
    }

    this.on('resize', window, throttle(this.handleResize.bind(this), 250, true), null, true);

    this.slider = new KeenSlider(this.refs.slider, this.getOptions());

    /**
     * Updates the slides
     */
    this.subscribe(events.sectionUpdate, async ({ sections }) => {
      const section = sections[getAttributeValue(this, 'section-id')];

      if (section) {
        const element = parseHtml(section, this.tagName.toLowerCase());
        const refs = getRefs(element);

        /**
         * Don't update elements if the slides are the same.
         */
        if (
          this.originalChildren &&
          JSON.stringify(Array.from(this.originalChildren).map(({ id }) => id)) ===
            JSON.stringify(Array.from(refs.slider.children).map(({ id }) => id))
        )
          return;

        await morph(this.refs.slider, refs.slider, true);

        this.slider.update(this.getOptions(), 0);

        /**
         * Starts the autoplay sliding, as we do in the intial `created` callback.
         */
        !this.paused && this.startAutoSlide(this.slider);
      }
    });
  }

  /**
   * Handles the window resize event.
   */
  handleResize() {
    if (
      (this.refs.slider.children.length <= 1 && isUntilBreakpoint('large')) ||
      (this.refs.slider.children.length <= 2 && isFromBreakpoint('large'))
    ) {
      this.paused = true;
    }
  }

  /**
   * Returns the slider options.
   * @returns {object}
   */
  getOptions() {
    return {
      loop: true,
      renderMode: 'performance',
      drag: true,
      mode: 'free',
      selector: Array.from(this.refs.slider.children),
      slides: {
        perView: this.refs.slider.children.length === 1 ? 1 : 1.05,
      },
      breakpoints: {
        [`(min-width: ${breakpoints.large}px)`]: {
          slides: {
            perView: 3.25,
          },
        },
      },

      /**
       * Slider was created.
       * @param {object} slider
       */
      created: ((slider) => {
        !this.paused && this.startAutoSlide(slider);
      }).bind(this),

      /**
       * The update function was called due to a size change or other trigger.
       * @param {object} slider
       */
      updated: ((slider) => {
        !this.paused && slider.moveToIdx(slider.track.details.abs + 5, true, this.animation);
      }).bind(this),

      /**
       * Animation has ended.
       * @param {object} slider
       */
      animationEnded: ((slider) => {
        !this.paused && slider.moveToIdx(slider.track.details.abs + 5, true, this.animation);
      }).bind(this),
    };
  }

  /**
   * Starts the autoplay sliding.
   * @param {object} slider
   */
  startAutoSlide(slider) {
    slider.moveToIdx(5, true, this.animation);
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
